import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { BlueprintRevealStatus } from '#src/__generated__/globalTypes.ts';
import { drawImage } from '#src/components/card/Overlay/utils/drawImage.ts';
import { range } from '#src/lib/arrays.ts';
import { dateProgress } from '#src/lib/dates.ts';
import { withFragments } from '#src/lib/gql/index.ts';

import type { earlyAccessOverlay2026_AnyCardInterface } from './__generated__/index.graphql.ts';
import delayedUrl from './assets/delayed.webp';
import lockedUrl from './assets/locked.webp';
import unlockedUrl from './assets/unlocked.webp';

export const earlyAccessOverlay2026 = withFragments(
  (card: earlyAccessOverlay2026_AnyCardInterface) => {
    const { blueprint } = card;
    if (!blueprint) {
      return undefined;
    }
    return async (image: ImageBitmap) => {
      const canvas = document.createElement('canvas');
      const WIDTH = image.width;
      const HEIGHT = image.height;

      canvas.width = WIDTH;
      canvas.height = HEIGHT;

      const state = blueprint.revealStatus;
      const progress = blueprint.revealDate
        ? dateProgress(
            blueprint.launchDate ?? new Date(2026, 6, 15),
            blueprint.revealDate
          ) / 100
        : 0;

      const ctx = canvas.getContext('2d')!;
      ctx.rect(0, 0, WIDTH, HEIGHT);
      ctx.fillStyle = '#000';
      ctx.fill();

      const promises: Promise<void>[] = [];

      const revealable = [
        BlueprintRevealStatus.REVEALABLE,
        BlueprintRevealStatus.REVEALED,
      ].includesValue(state);

      ctx.save();
      ctx.fillStyle = '#FFFFFF';

      const cappedProgress = Math.min(0.9, progress);

      const NB_STEPS = 9;
      range(Math.ceil(cappedProgress * NB_STEPS)).forEach((_, i) => {
        ctx.beginPath();
        ctx.arc(84 + 34 * i, 904, 3, 0, 2 * Math.PI);
        ctx.fill();
      });

      ctx.restore();

      if (revealable) {
        promises.push(
          drawImage({
            ctx,
            imageUrl: unlockedUrl,
            boundingBox: {
              left: 0,
              top: 0,
              width: 1,
              height: 1,
            },
          })
        );
      }

      if (state === BlueprintRevealStatus.DELAYED) {
        promises.push(
          drawImage({
            ctx,
            imageUrl: delayedUrl,
            boundingBox: {
              left: 0,
              top: 0,
              width: 1,
              height: 1,
            },
          })
        );
      }

      if (state === BlueprintRevealStatus.UNREVEALABLE) {
        promises.push(
          drawImage({
            ctx,
            imageUrl: lockedUrl,
            boundingBox: {
              left: 0,
              top: 0,
              width: 1,
              height: 1,
            },
          })
        );
      }

      await Promise.all(promises);

      return createImageBitmap(canvas);
    };
  },
  {
    AnyCardInterface: gql`
      fragment earlyAccessOverlay2026_AnyCardInterface on AnyCardInterface {
        slug
        blueprint {
          id
          launchDate
          revealDate
          revealStatus
        }
      }
    ` as TypedDocumentNode<earlyAccessOverlay2026_AnyCardInterface>,
  }
);
